import { CalendarMeetingLink } from '@/modules/calendarmeetinglink/models/CalendarMeetingLink';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { getters } from '@/modules/core/app/helpers/store';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import Lang from '@/modules/core/app/services/Lang';
import { MeetingStatus } from '@/modules/meeting/constants/MeetingStatus';
import { MeetingType } from '@/modules/meeting/constants/MeetingType';
import { MeetingUser } from '@/modules/meeting/models/MeetingUser';
import { User } from '@/modules/user/models/User';
import dayjs from 'dayjs';

export class Meeting extends BaseEntityModel {
  id?: number;
  modelId?: number;
  modelType?: string;
  calendarMeetingLinkId?: number;
  type: string;
  status?: string;
  scheduledEventUuid?: string;
  startedAt?: string;
  endedAt?: string;
  rescheduleUrl?: string;

  calendarMeetingLink?: Relationship<CalendarMeetingLink | null>;
  users?: Relationship<User[] | null>;
  meetingUsers?: Relationship<MeetingUser[] | null>;

  constructor(model: Partial<Meeting> = {}) {
    super(model);

    this.id = model.id;
    this.modelType = model.modelType;
    this.modelId = model.modelId;
    this.calendarMeetingLinkId = model.calendarMeetingLinkId;
    this.type = model.type as string;
    this.status = model.status;
    this.scheduledEventUuid = model.scheduledEventUuid;
    this.startedAt = model.startedAt;
    this.endedAt = model.endedAt;
    this.rescheduleUrl = model.rescheduleUrl;

    // @ts-ignore
    this.calendarMeetingLink = CalendarMeetingLink.mapModelOrNull(model.calendarMeetingLink);
    // @ts-ignore
    this.users = User.mapModelsOrNull(model.users);
    // @ts-ignore
    this.meetingUsers = MeetingUser.mapModelsOrNull(model.meetingUsers);
  }

  setId(id: number): void {
    this.id = id;
  }

  setScheduledEventUuid(scheduledEventUuid: string): void {
    this.scheduledEventUuid = scheduledEventUuid;
  }

  get needsToUpdate(): boolean {
    if ([MeetingType.FREE_CONSULT.key].includes(this.type)) {
      return false;
    }

    return true;
  }

  get typeEnum(): MeetingType | undefined {
    if (!this.type) {
      return;
    }

    return MeetingType.getByKey(this.type) as MeetingType;
  }

  get statusEnum(): MeetingStatus | undefined {
    if (!this.status) {
      return;
    }

    return MeetingStatus.getByKey(this.status) as MeetingStatus;
  }

  get displayName(): string {
    // @ts-ignore
    return this.typeEnum.value;
  }

  get formattedStatusText(): string {
    if (this.isBooked) {
      if (!this.startedAt) {
        return Lang.t('meeting.coming-up');
      }

      return (
        Lang.t('meeting.coming-up-on') +
        ' ' +
        dayjs(this.startedAt).format(DateFormat.MONTH_DAY_WITH_TWELVE_HOUR)
      );
    }

    if (this.isCompleted && this.endedAt) {
      return Lang.t('meeting.status-completed-on', {
        date: dayjs(this.endedAt).format(DateFormat.MONTH_DAY_WITH_TWELVE_HOUR)
      });
    }

    if (this.isCompleted || this.isCancelled || this.isSkipped) {
      return MeetingStatus.getByKey(this.status, 'key')?.value ?? '';
    }

    return '';
  }

  get attendees(): MeetingUser[] | undefined {
    if (!this.meetingUsers) {
      return undefined;
    }
    return this.meetingUsers.filter((meetingUser: MeetingUser) => meetingUser.hasAttended);
  }

  get hasRequiredDocuments(): boolean {
    return (
      this.isIntakeType ||
      this.isNotarizationType ||
      this.isWillNotarizationType ||
      this.isProtectionMandateNotarizationType
    );
  }

  get hasOnboardingScheduler(): boolean {
    if (this.isIntakeType && this.isAvailable) {
      return true;
    }

    return false;
  }

  get isLocked(): boolean {
    return (
      this.status === MeetingStatus.LOCKED.key && !getters.auth.getUser()?.isEstateProfessional
    );
  }

  get isAvailable(): boolean {
    return (
      this.status === MeetingStatus.AVAILABLE.key ||
      (this.status === MeetingStatus.LOCKED.key && !!getters.auth.getUser()?.isEstateProfessional)
    );
  }

  get isBooked(): boolean {
    return this.status === MeetingStatus.BOOKED.key;
  }

  get isCompleted(): boolean {
    return this.status === MeetingStatus.COMPLETED.key;
  }

  get isCancelled(): boolean {
    return this.status === MeetingStatus.CANCELLED.key;
  }

  get isSkipped(): boolean {
    return this.status === MeetingStatus.SKIPPED.key;
  }

  get isFreeConsult(): boolean {
    return this.type === MeetingType.FREE_CONSULT.key;
  }

  get isBasicSupportType(): boolean {
    return this.type === MeetingType.BASIC_SUPPORT.key;
  }

  get hasConsultType(): boolean {
    return this.freeConsultTypes.includes(this.type);
  }

  get hasPrefillWithPlannerType(): boolean {
    return this.prefillWithPlannerTypes.includes(this.type);
  }

  get isIntakeType(): boolean {
    return this.type === MeetingType.INTAKE.key;
  }

  get isNotarizationType(): boolean {
    return this.type === MeetingType.WILL_AND_PROTECTION_MANDATE_NOTARIZATION.key;
  }

  get isWillNotarizationType(): boolean {
    return this.type === MeetingType.WILL_NOTARIZATION.key;
  }

  get isGuidanceType(): boolean {
    return this.type === MeetingType.GUIDANCE.key;
  }

  get isProtectionMandateNotarizationType(): boolean {
    return this.type === MeetingType.PROTECTION_MANDATE_NOTARIZATION.key;
  }

  get isWillReviewType(): boolean {
    return this.type === MeetingType.WILL_REVIEW.key;
  }

  get isAnnualReviewType(): boolean {
    return this.type === MeetingType.ANNUAL_REVIEW.key;
  }

  get freeConsultTypes(): string[] {
    return [
      MeetingType.FREE_CONSULT.key,
      MeetingType.REFERRAL_CONSULT.key,
      MeetingType.REFERRAL_CONSULT_SETTLEMENT.key,
      MeetingType.SETTLEMENT_CONSULT.key
    ];
  }

  get prefillWithPlannerTypes(): string[] {
    return [
      MeetingType.INTAKE.key,
      MeetingType.GUIDANCE.key,
      MeetingType.WILL_REVIEW.key,
      MeetingType.ANNUAL_REVIEW.key
    ];
  }

  get calendarEventClasses(): string[] {
    const classes: string[] = [];

    if (this.isCompleted) {
      classes.push('event-container-success');
    } else if (this.isBooked) {
      classes.push('event-container-grey');
    } else if (this.isCancelled) {
      classes.push('event-container-danger');
    } else if (this.isSkipped) {
      classes.push('event-container-grey');
    }

    if (this.isSkipped) {
      classes.push('has-background-secondary');
    } else {
      classes.push('has-background-white');
    }

    return classes;
  }

  get calendarEventIcon(): string {
    if (this.isCompleted) {
      return 'cls-check-circle-1-filled';
    }
    if (this.isCancelled) {
      return 'cls-alert-circle';
    }
    if (this.isSkipped) {
      return 'cls-remove-circle';
    }

    return 'cls-check-circle-1';
  }

  get formattedStartedAt(): string {
    if (!this.startedAt) {
      return '';
    }

    return dayjs(this.startedAt).format(DateFormat.MONTH_DAY_WITH_TWELVE_HOUR);
  }

  get formattedEndedAt(): string {
    if (this.endedAt) {
      const date: string = dayjs(this.endedAt).format(
        DateFormat.FULL_MONTH_DAY_YEAR_WITH_TWELVE_HOUR
      );

      return Lang.t('meeting.completed-successfully-message-with-date', { date });
    }

    return Lang.t('meeting.completed-successfully-message');
  }

  getCalendarMeetingLinkUrlBasedOnUser(user: User): string | undefined {
    if (!this.calendarMeetingLink) {
      return;
    }

    return user.isLocaleEnglish
      ? this.calendarMeetingLink?.englishUrl
      : this.calendarMeetingLink?.frenchUrl;
  }

  getRescheduleMeetingLinkUrlBasedOnUser(user: User): string | undefined {
    if (this.isCompleted) {
      return user.isLocaleEnglish
        ? this.calendarMeetingLink?.englishUrl
        : this.calendarMeetingLink?.frenchUrl;
    }

    return this.rescheduleUrl;
  }
}
